import React, { useState } from 'react';
import { updateUser } from '../../../services/userServices';

interface NotificationToggleProps {
  /** Current user ID needed for the updateUser call */
  userId: string;
  /** Initial state passed from the parent or user profile data */
  initialEnabled?: boolean;
  /** Optional callback fired when the state changes successfully */
  onToggleSuccess?: (enabled: boolean) => void;
}

export const NotificationToggle: React.FC<NotificationToggleProps> = ({
  userId,
  initialEnabled = true,
  onToggleSuccess,
}) => {
  const [isEnabled, setIsEnabled] = useState<boolean>(initialEnabled);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleToggle = async () => {
    const nextState = !isEnabled;

    // 1. Optimistic Update (update UI immediately)
    setIsEnabled(nextState);
    setIsLoading(true);
    setError(null);

    try {
      // 2. Call your custom updateUser function
      const updatedUser = await updateUser(userId, {
        isNotificationEnabled: nextState,
      } as any);

      // 3. Update localStorage with the server response
      const storedUserRaw = localStorage.getItem('user');
      if (storedUserRaw) {
        const currentUser = JSON.parse(storedUserRaw);
        const newUserData = { ...currentUser, ...updatedUser };
        localStorage.setItem('user', JSON.stringify(newUserData));
      }

      // 4. Notify parent component if callback provided
      if (onToggleSuccess) {
        onToggleSuccess(nextState);
      }
    } catch (err: any) {
      // 5. Rollback state if API call fails
      setIsEnabled(!nextState);
      setError(err.message || 'Could not update preference. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-1 max-w-sm">
      <div className="flex items-center justify-between py-2">
        <span className="flex flex-col">
          <span className="text-sm font-medium text-gray-900">
            Notifications
          </span>
          <span className="text-xs text-gray-500">
            {isEnabled ? 'Enabled' : 'Disabled'}
          </span>
        </span>

        {/* Toggle Switch */}
        <button
          type="button"
          role="switch"
          aria-checked={isEnabled}
          disabled={isLoading}
          onClick={handleToggle}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 ${
            isEnabled ? 'bg-indigo-600' : 'bg-gray-200'
          } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              isEnabled ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        </button>
      </div>

      {/* Error Message */}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};